import { EddConfig, getEddConfig } from '@/data/config/eddConfig';
import { TYPES } from '@/data/constants/product';
import { useLeadTime } from '@/data/Content/LeadTime';
import { PickupDateState, resolvePickupDateState } from '@/data/Content/PickupDate';
import { useSettings } from '@/data/Settings';
import { ProductType } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { getHoursAndMinutes } from '@/data/utils/eddDateCalculation';
import { ComboStock, getPdpChannelAvailability } from '@/data/utils/eddInventory';
import { calculatePickupReadyTime } from '@/data/utils/eddPickupReadyTime';
import { isDeliveryOnlyInStore, isDeliveryOnlyProduct } from '@/data/utils/eddProductFlags';
import { getStoreCloseMinutes } from '@/data/utils/eddStoreCloseTime';
import { getProductMinQuantity } from '@/data/utils/getProductMinQuantity';
import { useIsClient } from '@/data/utils/useIsClient';
import { useMemo } from 'react';
import { getFulfillmentType, isMarketFulfilledProduct } from 'shared/custom/utils/inventoryUtils';
import { HomeDeliveryDateState } from './HomeDeliveryDate';

export type PdpPickupOption = PickupDateState & {
	/** AC5/AC6: units available for pickup. Omitted whenever the option is disabled. */
	availableQuantity?: number;
	/** AC7/AC8: only present alongside a same-day `with-date` result or a fixed
	 * next-day time for any other resolved day; absent for every other status. */
	pickupReadyTime?: string;
	/** 159071 AC19-AC20: the pickup cutoff time storeconf, for the footer legend
	 * when this option is the one currently selected. Omitted when disabled. */
	cutoffTime?: string;
	/** 159071 AC15/AC21: whether the footnote marker belongs next to this option's
	 * date line — true only for `with-date`; `available-no-date` shows no readiness
	 * line at all (AC13), so it never carries the marker either. Omitted when
	 * disabled. */
	hasCutoffFootnote?: boolean;
	/** Dropship / Special Order: the footer legend reads "order today" instead of
	 * naming a cutoff time. Omitted when disabled. */
	isMarketFulfilled?: boolean;
};

const isCounterVisible = (status: PickupDateState['status']): boolean =>
	status === 'with-date' || status === 'available-no-date';

/**
 * AC1: the same `ComboStock` decision the PLP resolver already knows how to read
 * (see `resolveComboPickupState` in `PickupDate.ts`), fed from the
 * already-aggregated per-kit-unit counters instead of per-component index fields.
 */
const derivePdpComboStock = (
	isCombo: boolean,
	storeAvailability: number
): ComboStock | undefined => {
	if (!isCombo) return undefined;
	if (storeAvailability >= 1) return 'all-in-store';
	return 'incomplete';
};

/**
 * AC7/AC8: only a same-day result gets the calculated SLA time; any other resolved
 * day uses the fixed next-day ready time, and every non-`with-date` status
 * (including `available-no-date`, which has no date to attach a ready time to)
 * gets none.
 */
type DerivedReadyTime = {
	pickupReadyTime: string | undefined;
	exceedsLatest: boolean;
};

export const derivePickupReadyTime = (
	config: EddConfig,
	pickupState: PickupDateState | HomeDeliveryDateState,
	physicalStoreId?: string
): DerivedReadyTime => {
	const now = new Date();
	const { hours, minutes } = getHoursAndMinutes(now);
	const rawHours = hours + config.pickupSlaHours + (minutes > 0 ? 1 : 0);
	const storeCloseMinutes = getStoreCloseMinutes(physicalStoreId, now);

	if (storeCloseMinutes === undefined) {
		return { pickupReadyTime: config.pickupNextDayReadyTime, exceedsLatest: true };
	}

	// Last acceptable ready time: one hour before the store closes.
	const closeMinutes = storeCloseMinutes - 60;

	const storeCloseTime = `${String(Math.floor(closeMinutes / 60)).padStart(2, '0')}:${String(
		closeMinutes % 60
	).padStart(2, '0')}`;

	const pickupReadyTime = calculatePickupReadyTime(
		config.pickupSlaHours,
		config.pickupNextDayReadyTime,
		now,
		storeCloseTime
	);

	if (rawHours * 60 >= closeMinutes) {
		return { pickupReadyTime, exceedsLatest: true };
	}

	if (pickupState.status !== 'with-date')
		return { pickupReadyTime: undefined, exceedsLatest: false };
	if (pickupState.descriptor !== 'today') return { pickupReadyTime, exceedsLatest: false };

	return {
		pickupReadyTime,
		exceedsLatest: false,
	};
};

/**
 * PDP pickup-in-store option (delivery-options story, AC1-AC9).
 *
 * AC1/AC2: the eligibility tree, cutoff, and date calculation are the exact same
 * `resolvePickupDateState` story 159042's PLP hook (`usePickupDate`) calls — not
 * duplicated or forked. Only the inventory INPUT differs (AC3): `storeAvail` is the
 * `ProductAvailabilityData` record the PDP's own inventory-availability call
 * already resolves (see `ProductAvailability.tsx`'s `derivedState.storeAvail`),
 * already combo-normalized by `getStoreAvailabilityKit` when the product is a kit
 * — so this hook reads it identically whether `product` is a standalone SKU or a
 * combo, with no separate combo code path required on this surface.
 *
 * AC24/AC25/AC28 (158621): resolution happens on the client at hydration, same
 * `pending` convention as the PLP hooks.
 */
export const usePdpPickupOption = (
	product: ProductType | undefined,
	physicalStoreId: string,
	storeAvail: ProductAvailabilityData | undefined
): PdpPickupOption => {
	const isClient = useIsClient();
	const leadTime = useLeadTime(product, physicalStoreId); // AC1: leadtime retrieval unchanged
	const { settings } = useSettings();

	const fulfillmentType = useMemo(
		() =>
			getFulfillmentType(
				product?.attributes,
				(product?.ribbons ?? []).map((ribbon) => ribbon.identifier)
			),
		[product?.attributes, product?.ribbons]
	);

	const { storeAvailability, marketAvailability } = useMemo(
		() => getPdpChannelAvailability(storeAvail),
		[storeAvail]
	);

	// eslint-disable-next-line complexity
	return useMemo(() => {
		if (!isClient) {
			return { status: 'pending' };
		}

		const config = getEddConfig(settings.userData);
		const isCombo = product?.type === TYPES.kit;
		const comboStock = derivePdpComboStock(isCombo, storeAvailability);

		const nominalQuantity = product ? getProductMinQuantity(product) : 1;
		const minQuantity =
			Number.isFinite(nominalQuantity) && nominalQuantity > 1 ? nominalQuantity : 1;

		const pickupState = resolvePickupDateState(config, {
			// 163615 AC1-AC2: corporate attribute OR the selected store's flag. Resolved
			// here, before the stock check below, so "Exclusivo para envío a domicilio"
			// wins over "Agotado" (AC5).
			isDeliveryOnly: isDeliveryOnlyInStore(product, physicalStoreId),
			fulfillmentType,
			isCombo,
			comboStock,
			leadTime,
			minQuantity,
			storeAvailability,
			marketAvailability,
			allowNoDateWithoutLeadTime: true,
		});

		// Combo availability story, AC2(b)/AC3: pickup is available for a combo only
		// if at least one component isn't itself flagged delivery-only -- the
		// "every component has inventory" half of AC2 is already carried by
		// `comboStock` above (derived from the blended, min-across-components
		// counter), so this is purely the catalog-attribute half. `every()` on an
		// empty array is vacuously true, hence the length guard.
		const kitComponents = product?.components ?? [];
		const comboBlocksPickup =
			isCombo &&
			kitComponents.length > 0 &&
			kitComponents.every((component) => isDeliveryOnlyProduct(component.attributes));

		if (comboBlocksPickup) {
			return { status: 'not-available' };
		}

		if (isCounterVisible(pickupState.status) && storeAvailability <= 0) {
			return { status: 'no-label' };
		}

		// AC5: "A disabled option returns no counter." The precise set of disabled
		// states is defined by story 159073, which this hook doesn't have visibility
		// into — treated here as every status other than the two actionable ones.
		if (!isCounterVisible(pickupState.status)) {
			return pickupState;
		}

		const { pickupReadyTime, exceedsLatest } = derivePickupReadyTime(
			config,
			pickupState,
			physicalStoreId
		);

		const resolvedState: PickupDateState =
			exceedsLatest && pickupState.status === 'with-date'
				? { ...pickupState, descriptor: 'tomorrow' }
				: pickupState;

		// 159071 AC21: every pickup `with-date` path (Step 2, Step 3, combo) already
		// runs through `isPastCutoff(config.pickupCutoffTime)`, so the marker is tied
		// 1:1 to `with-date` here; `available-no-date` never shows a date line (AC13).
		const hasCutoffFootnote = resolvedState.status === 'with-date';

		return {
			...resolvedState,
			availableQuantity: storeAvailability,
			pickupReadyTime,
			cutoffTime: config.pickupCutoffTime,
			hasCutoffFootnote,
			isMarketFulfilled: isMarketFulfilledProduct(product),
		};
	}, [
		isClient,
		settings.userData,
		product,
		fulfillmentType,
		leadTime,
		storeAvailability,
		marketAvailability,
		physicalStoreId,
	]);
};
